import L from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import afMarkerIcon from 'assets/topology/AF_Network_Element.svg';
import cfMarkerIcon from 'assets/topology/CF_Network_Element.svg';
import { Marker } from 'react-leaflet';
import './map.scss';
import cloneDeep from 'lodash.clonedeep';
import { renderToString } from 'react-dom/server';
import { Buffer } from 'buffer';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import 'leaflet-responsive-popup';
import 'leaflet-responsive-popup/leaflet.responsive.popup.css';
import { createNeRowItem, createRowHeader } from './utils/tooltipData';

type RouterParams = {
  history: string;
};

type IconProps = {
  text: string;
  icon: any;
  showLeaderAMDFBadge?: boolean;
};

type VendorIconProps = {
  text: string;
  icon: any;
  count: number;
};

const mapState = (state: RootState) => ({
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  enabledCollectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state)
});
const mapDispatch = {
  goToAccessFunctionView: actions.goToAccessFunctionView,
  selectAccessFunction: actions.selectAccessFunction,
  selectCollectionFunction: collectionFunctionActions.selectCollectionFunction,
  goToCollectionFunctionView:
    collectionFunctionActions.goToCollectionFunctionView
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

type Props = {
  id?: any;
  position: any;
  count?: any;
  type: string;
  details: any;
  // status: string,
  onDragEnd?: any,
  onHover?: any,
} & PropsFromRedux &
  PropsFromRouter;

// Define your custom marker icon as a React component
export const CustomMarkerIcon = ({ text, icon, showLeaderAMDFBadge = false }: IconProps) => (
  <div className="ne">
    <span>
      {showLeaderAMDFBadge && <div className="badge badge-leader-admf">M</div>}
      <img src={icon} alt={text} className="marker-shadow" />
    </span>
    <span className="display-text">{text}</span>
  </div>
);

export const XcipioMarkerIcon = (icon: string, badgeClass: string, childCount: number, showLeaderAMDFBadge = false) => (
  <React.Fragment>
    <img src={icon} className="marker-shadow" alt="cluster" />
    <div className={`badge ${badgeClass}`}>
      {childCount}
    </div>
    {showLeaderAMDFBadge && <div className="badge badge-leader-admf">M</div>}
  </React.Fragment>
);

export const CustomVendorMarkerIcon = ({
  text,
  icon,
  count
}: VendorIconProps) => (
  <div className="vendor-ne">
    <img src={icon} alt={text} />
    <div className="badge">{count}</div>
    <span>{text}</span>
  </div>
);

const getStatusIconClass = (status: string) => {
  let ret = 'cluster-row-failed';
  if (status === 'ACTIVE') {
    ret = 'cluster-row-active';
  } else if (status === 'INACTIVE' || status === 'UNKNOWN') {
    ret = 'cluster-row-inactive';
  } else if (status === 'FAILED') {
    ret = 'cluster-row-failed';
  }
  return ret;
};

const NetworkElement = ({
  position,
  count,
  id,
  type,
  details,
  history,
  enabledAccessFunctions,
  enabledCollectionFunctionTemplates,
  goToAccessFunctionView,
  selectAccessFunction,
  selectCollectionFunction,
  goToCollectionFunctionView,
  onDragEnd,
  onHover
}: Props) => {
  const images = require.context('../../assets/topology/', true, /\.svg$/);
  const { data } = details;
  const markerRef = useRef<L.Marker>(null);

  useEffect(() => {
    if (markerRef && markerRef.current) {
      const marker = markerRef.current as L.Marker;
      const popup = L.responsivePopup({ offset: [20, 20] }).setContent(renderPopup(details));
      marker.bindPopup(popup);
    }
  }, [markerRef, enabledCollectionFunctionTemplates]);

  // Define a custom function to create the icon using the React component
  const createCustomMarkerIcon = (text: string, ne: string, type: string) => {
    const imageName = ne === 'AF' ? `AF/AF-${type}.svg` : `CF/CF-${type}.svg`;
    if (ne === 'CF') {
      // console.log(imageName);
    }
    let image;
    try {
      image = images(`./${imageName}`);
    } catch (e) {
      image = ne === 'AF' ? afMarkerIcon : cfMarkerIcon;
    }
    // if (!image) {
    //   image = ne === 'AF' ? afMarkerIcon : cfMarkerIcon;
    // }
    const trimmedText = text.length > 12 ? `${text.substring(0, 12)}...` : text;
    const html = renderToString(<CustomMarkerIcon text={trimmedText} icon={image} />);
    const divIcon = L.divIcon({ html, className: 'ne-div-class' });
    return divIcon;
  };

  const handleGoToPOIPage = (): void => {
    const poi = cloneDeep(data);
    poi.id = Buffer.from(poi.id, 'binary').toString('base64');
    selectAccessFunction([poi]);
    goToAccessFunctionView(history);
  };

  const handleGoToCFPage = (): void => {
    const cf = cloneDeep(data);
    cf.id = Buffer.from(cf.id, 'binary').toString('base64');
    selectCollectionFunction([cf]);
    goToCollectionFunctionView(history);
  };

  const renderPopup = (data: any) : HTMLDivElement => {
    if (type === 'AF') {
      return renderAFPopup(data);
    }
    return renderCFPopup(data);
  };

  const renderAFPopup = (afDetails: any) : HTMLDivElement => {
    const af = afDetails.data;
    if (type !== 'AF') return L.DomUtil.create('div');
    const afName = af.name;
    let vendor = '';
    let sourceIp;
    let sourcePort;
    let disableLinkClass = '';

    if (af.provisioningInterfaces && af.provisioningInterfaces.length > 0) {
      sourceIp = af.provisioningInterfaces[0].options.find(
        (o: any) => o.key === 'destIPAddr'
      );
      if (!sourceIp) {
        sourceIp = af.provisioningInterfaces[0].options.find(
          (o: any) => o.key === 'uri'
        );
      }
      sourcePort = af.provisioningInterfaces[0].options.find(
        (o: any) => o.key === 'destPort'
      );
    }

    if (
      af.provisioningInterfaces.length === 0 &&
      af.dataInterfaces.length === 0 &&
      af.contentInterfaces.length === 0
    ) {
      disableLinkClass = 'disable-link';
    }

    if (enabledAccessFunctions && enabledAccessFunctions[af.type]) {
      vendor = enabledAccessFunctions[af.type].vendor || '';
    }

    const popupNe = L.DomUtil.create('div', 'popup-ne');
    const popupContainerNe = L.DomUtil.create('div', 'popup-container-ne');
    if (afName) {
      createRowHeader(popupContainerNe, afDetails, afName);
    }
    // if (af.id) {
    //   createNeRowItem(popupContainerNe, 'ID: ', af.id);
    // }
    if (afDetails.location && afDetails.location.city &&
      afDetails.location.stateCode) {
        createNeRowItem(popupContainerNe, 'Location: ', `${afDetails.location.city}, ${afDetails.location.stateCode}.`);
    }
    if (sourceIp) {
      createNeRowItem(popupContainerNe, 'IP Address: ', sourceIp.value);
    }
    if (af.serialNumber) {
      createNeRowItem(popupContainerNe, 'Serial: ', af.serialNumber);
    }
    if (vendor) {
      createNeRowItem(popupContainerNe, 'Vendor: ', vendor);
    }
    if (af.type) {
      createNeRowItem(popupContainerNe, 'Type: ', af.type);
    }
    if (af.model) {
      createNeRowItem(popupContainerNe, 'Model: ', af.model);
    }
    if (af.version) {
      createNeRowItem(popupContainerNe, 'Version: ', af.version);
    }

    const popupNeFooter = L.DomUtil.create('div', 'popup-ne-footer', popupContainerNe);
    const navigationLink = L.DomUtil.create('a', 'bp3-minimal bp3-button bp3-intent-primary', popupNeFooter);
    navigationLink.setAttribute('role', 'button');
    navigationLink.innerHTML = 'Go to POI Page';

    L.DomEvent.on(navigationLink, 'click', () => {
      handleGoToPOIPage();
    });

  return popupContainerNe;
  };

  const renderCFPopup = (cfDetails: any) : HTMLDivElement => {
    const cf = cfDetails.data;
    const { name, type, id } = cf;
    const cfGroupName = cf?.cfGroup?.name ?? '';
    let ipAddress = '';
    let version = '';

    if (cf.hi2Interfaces && cf.hi2Interfaces.length > 0) {
      ipAddress = cf.hi2Interfaces[0]?.options?.find((x: any) => x.key === 'destIPAddr').value;
      version = cf.hi2Interfaces[0]?.options?.find((x: any) => x.key === 'version').value;
    }

    const popupNe = L.DomUtil.create('div', 'popup-ne');
    const popupContainerNe = L.DomUtil.create('div', 'popup-container-ne', popupNe);
    if (name) {
      createRowHeader(popupContainerNe, cfDetails, name);
    }
    // if (id) {
    //   createNeRowItem(popupContainerNe, 'ID: ', id);
    // }
    if (cfDetails.location && cfDetails.location.city && cfDetails.location.stateCode) {
      createNeRowItem(popupContainerNe, 'Location: ', `${cfDetails.location.city}, ${cfDetails.location.stateCode}`);
    }
    if (ipAddress) {
      createNeRowItem(popupContainerNe, 'IP Address: ', ipAddress);
    }
    if (type) {
      createNeRowItem(popupContainerNe, 'Type: ', type);
    }
    if (version) {
      createNeRowItem(popupContainerNe, 'Version: ', version);
    }
    if (cfGroupName) {
      createNeRowItem(popupContainerNe, 'CF Group: ', cfGroupName);
    }

    const popupNeFooter = L.DomUtil.create('div', 'popup-ne-footer', popupContainerNe);
    const navigationLink = L.DomUtil.create('a', 'go-to-btn bp3-minimal bp3-button bp3-intent-primary', popupNeFooter);
    navigationLink.setAttribute('role', 'button');
    navigationLink.innerHTML = 'Go to CF Page';
    if (enabledCollectionFunctionTemplates[cf.tag]) {
      L.DomEvent.on(navigationLink, 'click', () => {
        handleGoToCFPage();
      });
    } else {
      const captionTxt = L.DomUtil.create('span', 'css-tooltiptext', popupNeFooter);
      captionTxt.innerHTML = 'Missing CF template.';
      popupNeFooter.classList.add('css-tooltip');
      navigationLink.classList.add('bp3-disabled');
      navigationLink.setAttribute('disabled', '');
    }
    return popupNe;
  };

  // Create the custom marker icon with the required html
  const icon = createCustomMarkerIcon(data.name, type, details.poiType);

  const onClick = (event: any) => {
    const clickedElement = event.originalEvent.target;
    if (markerRef && markerRef.current) {
      const marker = markerRef.current as L.Marker;
      if (clickedElement.tagName !== 'IMG') {
        /*
        In useEffect hook, when bindPopup is called Leaflet automatically associates its own default callback for opening and closing popup.
        Leaflet's default callback is executed prior to our custom onClick callback defined here. Specifically, when a user clicks on the popup trigger:
        1. Leaflet's default callback is triggered and opens the popup.
        2. Our custom onClick callback is invoked to close the popup.
        */
        marker.closePopup();
      }
    }
  }

  return (
    <Marker
      data-status={details.status}
      data-original={details}
      key={id}
      position={position}
      icon={icon}
      alt={type}
      title={data.type}
      draggable={false}
      eventHandlers={{ dragend: onDragEnd, mouseover: onHover, click: onClick }}
      ref={markerRef}
    />
  );
};

export default withRouter(connector(NetworkElement));
